import type { SalesChannel } from '@/types/integration';
import { Box, Button, Card, CardHeader, Table, TableBody, TableHead, TableCell, TableRow, CardContent } from '@mui/material';
import { BusyIndeterminateInline, BusyIndeterminateMini } from '../../components/misc/busy';
import { getTodayDate } from '../../components/misc/get-today-date';
import { useEffect, useRef } from 'react';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NextLink from 'next/link';
import toast from 'react-hot-toast';
import { findSalesChannelById } from '../../contexts/IntegrationsContext';
import { blankImg } from '../../components/misc/blank-img';
import { useField } from '../../hooks/useField';
import { getActiveOrderCounts } from '@/endpoints/dashboard/getActiveOrderCounts';
import { useCommittedOrganization } from '@/session/OrganizationIdentityContext';
import { requestScopeFor } from '@/session/scopedTransport';
import { utils } from '../../utils';
import { acquireOperationContext, operationRequirement } from '@/session/operation';

type CountDef = {
  title: string;
  key: string;
  apiPath: string;
  showWarning?: boolean;
};

type OrderCountData = {
  sales_channel_id: string | number;
  num_orders: number;
  late_orders: number;
};

type OrderCounts = {
  [key: string]: number;
};

type OrderBusy = {
  [key: string]: boolean;
};

export interface OverviewActiveOrdersProps {
  timezone: string;
  sChannels: SalesChannel[];
  sChannelsLoading: boolean;
}

export const OverviewActiveOrders: React.FC<OverviewActiveOrdersProps> = ({ timezone, sChannels, sChannelsLoading }) => {
  const organization = useCommittedOrganization();
  const requestSequenceRef = useRef(0);

  const orderBusy = useField<OrderBusy>({});
  const orderCounts = useField<OrderCounts>({});

  const formatNumber = (num: number | undefined): string => {
    if (num === undefined || num === null) return '0';
    return num.toLocaleString('en-US');
  };

  const getCountDefs = (timezone: string): CountDef[] => {
    if (!timezone) return [];

    return [
      {
        title: 'Unshipped',
        key: 'unshipped',
        apiPath: `/search/order/count?status=routed,partially_routed,partially_shipped,pending&q=*`,
      },
      {
        title: 'Late',
        key: 'late',
        apiPath: `/search/order/count?status=routed,partially_routed,partially_shipped,pending&requested_ship_date=${getTodayDate()}:lt&tz=${timezone}&q=*`,
        showWarning: true,
      },
    ];
  };

  const getOrderCounts = async (): Promise<void> => {
    if (!timezone) return;
    const operation = acquireOperationContext(organization);
    if (!operation.isScopeCurrent()) return;
    const sequence = (requestSequenceRef.current += 1);

    const response = await getActiveOrderCounts(
      requestScopeFor(organization),
      { timezone },
      {
        signal: operation.scopeSignal,
        operationRequirement: operationRequirement(operation, 'scope'),
      }
    );
    if (sequence !== requestSequenceRef.current || !operation.isScopeCurrent()) return;

    if (response.isError) {
      if (!utils.error.isCanceledResponse(response)) toast.error(response.errors.error ?? 'Failed to get active order counts');
      return;
    }

    let cnts = response.result;
    const countDefs = getCountDefs(timezone);

    let tmpOrderCounts: OrderCounts = {};
    sChannels.map((sc, index) => {
      let cntrec = cnts.find((c: OrderCountData) => c.sales_channel_id == sc.id);
      if (!cntrec) {
        cntrec = {
          sales_channel_id: sc.id,
          num_orders: 0,
          late_orders: 0,
        };
      }
      countDefs.map((cd) => {
        tmpOrderCounts[cd.title + '_' + index] = cd.title == 'Unshipped' ? cntrec.num_orders : cntrec.late_orders;
      });
    });
    orderCounts.setValue(tmpOrderCounts);
  };

  useEffect(() => {
    if (sChannels && sChannels.length) {
      orderCounts.setValue({});
      void getOrderCounts();
    }

    return () => {
      requestSequenceRef.current += 1;
    };
  }, [organization, sChannels, timezone]);

  return (
    <Card>
      <CardHeader sx={{ p: 2 }} title='Active Orders' />
      {sChannelsLoading && (!sChannels || !sChannels.length) && (
        <CardContent>
          <BusyIndeterminateMini altSize={undefined} />
        </CardContent>
      )}
      {!!(sChannels && sChannels.length) && (
        <Box sx={{ overflow: 'auto' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sales Channel</TableCell>
                <TableCell align='center' sx={{ minWidth: '6rem' }}>
                  Unshipped
                </TableCell>
                <TableCell align='center' sx={{ minWidth: '6rem' }}>
                  Late
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sChannels.map((sc, index) => (
                <TableRow key={sc.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      {(() => {
                        const channel = findSalesChannelById(sChannels, sc.id);
                        const iconUrl = channel?.borrowed_icon_url || blankImg;
                        return (
                          <Box
                            sx={{
                              display: 'flex',
                              width: '2.5rem',
                              minWidth: '2.5rem',
                              p: 0.5,
                              backgroundColor: 'white',
                              borderRadius: '.5rem',
                            }}
                          >
                            <img src={iconUrl} width={32} height={32} alt='' />
                          </Box>
                        );
                      })()}
                      <Box
                        sx={{
                          wordBreak: 'break-word',
                          whiteSpace: 'normal',
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: '0.875rem',
                            fontWeight: 500,
                            lineHeight: 1.2,
                          }}
                        >
                          {sc.name}
                        </Box>
                        <Box
                          sx={{
                            fontSize: '0.75rem',
                            opacity: 0.75,
                            lineHeight: 1.2,
                            mt: 0.25,
                          }}
                        >
                          Sub: {sc.integration_subscription_id}
                        </Box>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell align='center' sx={{ minWidth: '6rem' }}>
                    {orderBusy.value['Unshipped'] && !('Unshipped_' + index in orderCounts.value) && (
                      <BusyIndeterminateInline justify='center' color='primary' height='1rem' />
                    )}
                    {orderCounts.value['Unshipped_' + index] && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        {orderCounts.value['Unshipped_' + index] > 0 ? (
                          <Button component={NextLink} href={`/${organization}/orders?tab=unshipped&sc=${sc.id}`} size='small' sx={{ gap: 0.5 }}>
                            <Box>{formatNumber(orderCounts.value['Unshipped_' + index])}</Box>
                          </Button>
                        ) : (
                          <Box>{formatNumber(orderCounts.value['Unshipped_' + index])}</Box>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align='center' sx={{ minWidth: '6rem' }}>
                    {orderBusy.value['Late'] && !('Late_' + index in orderCounts.value) && (
                      <BusyIndeterminateInline justify='center' color='primary' height='1rem' />
                    )}
                    {orderCounts.value['Late_' + index] && (
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        {orderCounts.value['Late_' + index] > 0 ? (
                          <Button component={NextLink} href={`/${organization}/orders?tab=late&sc=${sc.id}`} size='small' sx={{ gap: 0.5 }}>
                            <WarningAmberIcon color='error' fontSize='small' />
                            <Box>{formatNumber(orderCounts.value['Late_' + index])}</Box>
                          </Button>
                        ) : (
                          <Box>{formatNumber(orderCounts.value['Late_' + index])}</Box>
                        )}
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Card>
  );
};
